import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${comic.slug}`}
        className="group flex items-center gap-3 p-3 rounded-xl bg-background-50 hover:bg-background-100 transition-all cursor-pointer"
      >
        <div className="w-14 h-[74px] flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={comic.cover}
            alt={`${comic.title} 漫画封面`}
            title={`${comic.title} - 韩国漫画网`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
            {comic.title}
          </h3>
          <p className="text-xs text-foreground-500 truncate mt-0.5">{comic.category}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="flex items-center gap-0.5 text-xs text-accent-600">
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-star-fill text-[10px]"></i></span>
              {comic.rating}
            </span>
            <span className="text-xs text-foreground-400">{comic.status}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${comic.slug}`}
        className="group relative flex flex-col rounded-2xl overflow-hidden cursor-pointer bg-background-100"
      >
        <div className="relative w-full aspect-[4/3] overflow-hidden">
          <img
            src={comic.banner}
            alt={`${comic.title} 横幅`}
            title={`${comic.title} - 免费在线韩漫`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <div className="flex items-center gap-2 mb-2">
              {comic.isHot && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary-500 text-background-50 whitespace-nowrap">
                  HOT
                </span>
              )}
              {comic.isNew && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-accent-500 text-background-50 whitespace-nowrap">
                  NEW
                </span>
              )}
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/20 text-white backdrop-blur-sm whitespace-nowrap">
                {comic.category}
              </span>
            </div>
            <h3 className="text-lg font-bold text-white font-heading">{comic.title}</h3>
            <p className="text-sm text-white/80 mt-1 line-clamp-2">{comic.description}</p>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${comic.slug}`}
      className="group flex flex-col rounded-xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all cursor-pointer"
    >
      <div className="relative w-full aspect-[3/4] overflow-hidden">
        <img
          src={comic.cover}
          alt={`${comic.title} 漫画封面`}
          title={`${comic.title} - 韩国漫画网免费在线韩漫`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
        />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {comic.isHot && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary-500 text-background-50 whitespace-nowrap">
              HOT
            </span>
          )}
          {comic.isNew && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-accent-500 text-background-50 whitespace-nowrap">
              NEW
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
            comic.status === '连载中'
              ? 'bg-secondary-500 text-background-50'
              : 'bg-foreground-600 text-background-100'
          }`}>
            {comic.status}
          </span>
        </div>
      </div>
      <div className="p-3 md:p-4 flex flex-col gap-2">
        <h3 className="text-sm md:text-base font-bold text-foreground-900 group-hover:text-primary-600 transition-colors truncate font-heading">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
          {comic.description}
        </p>
        <div className="flex items-center justify-between mt-auto pt-1">
          <span className="flex items-center gap-0.5 text-xs text-accent-600 font-medium">
            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-star-fill text-xs"></i></span>
            {comic.rating}
          </span>
          <span className="text-xs text-foreground-400">{comic.totalChapters}话</span>
        </div>
      </div>
    </Link>
  );
}